/**
 * Helpers de Date - Normalisation et Manipulation
 * 
 * Principe KISS: Fonctions simples et prévisibles
 * Principe SOLID: Single Responsibility - Manipulation de dates uniquement
 * 
 * Toutes les fonctions utilisent le fuseau horaire LOCAL pour éviter
 * les problèmes de décalage UTC.
 */

import {
  startOfDay,
  isBefore,
  isAfter,
  isSameDay,
  isToday,
  isPast,
  isFuture,
  addDays,
  addMonths,
  addYears,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  differenceInDays,
  format,
  parse,
  isValid,
  startOfToday,
} from "date-fns";
import { getDateLocale } from "@/lib/i18n/formatLocale";

// ============================================================================
// NORMALISATION
// ============================================================================

/**
 * Normalise une date au début de la journée (00:00:00)
 * Évite les problèmes de timezone en utilisant startOfDay
 */
export function normalizeDate(date: Date): Date {
  if (!isValid(date)) {
    throw new Error("Date invalide");
  }
  return startOfDay(date);
}

/**
 * Normalise une plage de dates
 */
export function normalizeDateRange(range: { from?: Date; to?: Date }): {
  from: Date | undefined;
  to: Date | undefined;
} {
  return {
    from: range.from ? normalizeDate(range.from) : undefined,
    to: range.to ? normalizeDate(range.to) : undefined,
  };
}

// ============================================================================
// FORMATAGE
// ============================================================================

/**
 * Format une date en YYYY-MM-DD (format local, pas UTC)
 * Utilisé pour les clés de map et les requêtes DB
 */
export function formatDateLocal(date: Date): string {
  const normalized = normalizeDate(date);
  const year = normalized.getFullYear();
  const month = String(normalized.getMonth() + 1).padStart(2, "0");
  const day = String(normalized.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Format une date pour la base de données (ISO string local)
 * Préférer formatDateLocal pour les clés, cette fonction pour les valeurs
 */
export function formatDateForDB(date: Date): string {
  const normalized = normalizeDate(date);
  // Utiliser toISOString mais ajuster pour le fuseau local
  const year = normalized.getFullYear();
  const month = String(normalized.getMonth() + 1).padStart(2, "0");
  const day = String(normalized.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}T00:00:00`;
}

/**
 * Format une date pour l'affichage utilisateur.
 * @param lang - Code langue (défaut "fr"). Passer i18n.language dans les composants.
 */
export function formatDateForDisplay(
  date: Date,
  formatStr: string = "d MMM yyyy",
  lang?: string
): string {
  return format(normalizeDate(date), formatStr, { locale: getDateLocale(lang ?? "fr") });
}

/**
 * Parse une date depuis une string YYYY-MM-DD
 */
export function parseDateLocal(dateString: string): Date {
  const parsed = parse(dateString, "yyyy-MM-dd", new Date());
  if (!isValid(parsed)) {
    throw new Error(`Date invalide: ${dateString}`);
  }
  return normalizeDate(parsed);
}

// ============================================================================
// COMPARAISONS
// ============================================================================

/**
 * Vérifie si une date est dans une plage (inclusif)
 */
export function isDateInRange(
  date: Date,
  start: Date,
  end: Date
): boolean {
  const normalizedDate = normalizeDate(date);
  const normalizedStart = normalizeDate(start);
  const normalizedEnd = normalizeDate(end);
  
  return (
    (isSameDay(normalizedDate, normalizedStart) ||
      isAfter(normalizedDate, normalizedStart)) &&
    (isSameDay(normalizedDate, normalizedEnd) ||
      isBefore(normalizedDate, normalizedEnd))
  );
}

/**
 * Vérifie si une date est avant une autre (strict)
 */
export function isDateBefore(date: Date, compareTo: Date): boolean {
  return isBefore(normalizeDate(date), normalizeDate(compareTo));
}

/**
 * Vérifie si une date est après une autre (strict)
 */
export function isDateAfter(date: Date, compareTo: Date): boolean {
  return isAfter(normalizeDate(date), normalizeDate(compareTo));
}

/**
 * Vérifie si deux dates sont identiques (même jour)
 */
export function isDateEqual(date1: Date, date2: Date): boolean {
  return isSameDay(normalizeDate(date1), normalizeDate(date2));
}

/**
 * Vérifie si une date est aujourd'hui
 */
export function isDateToday(date: Date): boolean {
  return isToday(normalizeDate(date));
}

/**
 * Vérifie si une date est dans le passé
 */
export function isDatePast(date: Date): boolean {
  return isPast(normalizeDate(date));
}

/**
 * Vérifie si une date est dans le futur
 */
export function isDateFuture(date: Date): boolean {
  return isFuture(normalizeDate(date));
}

// ============================================================================
// CALCULS
// ============================================================================

/**
 * Calcule le nombre de jours entre deux dates (inclusif)
 */
export function calculateDuration(start: Date, end: Date): number {
  const normalizedStart = normalizeDate(start);
  const normalizedEnd = normalizeDate(end);
  
  if (isBefore(normalizedEnd, normalizedStart)) {
    return 0;
  }
  
  return differenceInDays(normalizedEnd, normalizedStart) + 1;
}

/**
 * Génère toutes les dates d'une plage
 */
export function getDatesInRange(start: Date, end: Date): Date[] {
  const normalizedStart = normalizeDate(start);
  const normalizedEnd = normalizeDate(end);
  
  if (isBefore(normalizedEnd, normalizedStart)) {
    return [];
  }
  
  return eachDayOfInterval({
    start: normalizedStart,
    end: normalizedEnd,
  });
}

/**
 * Obtient le premier jour disponible à partir d'une date
 */
export function getFirstAvailableDate(
  fromDate: Date,
  isDateAvailable: (date: Date) => boolean,
  maxDays: number = 365
): Date | null {
  const normalized = normalizeDate(fromDate);
  
  for (let i = 0; i < maxDays; i++) {
    const checkDate = addDays(normalized, i);
    if (isDateAvailable(checkDate)) {
      return checkDate;
    }
  }
  
  return null;
}

// ============================================================================
// UTILITAIRES CALENDRIER
// ============================================================================

/**
 * Obtient le premier jour du mois
 */
export function getMonthStart(date: Date): Date {
  return startOfMonth(normalizeDate(date));
}

/**
 * Obtient le dernier jour du mois
 */
export function getMonthEnd(date: Date): Date {
  return endOfMonth(normalizeDate(date));
}

/**
 * Obtient le premier jour de la semaine (lundi)
 */
export function getWeekStart(date: Date): Date {
  return startOfWeek(normalizeDate(date), { weekStartsOn: 1 });
}

/**
 * Obtient le dernier jour de la semaine (dimanche)
 */
export function getWeekEnd(date: Date): Date {
  return endOfWeek(normalizeDate(date), { weekStartsOn: 1 });
}

/**
 * Génère les jours d'un mois pour le calendrier
 * Retourne un tableau avec des null pour les jours de padding
 */
export function getCalendarDays(month: Date): (Date | null)[] {
  const monthStart = getMonthStart(month);
  const monthEnd = getMonthEnd(month);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Calculer le padding pour commencer le lundi (jour 1)
  let startOffset = monthStart.getDay() - 1;
  if (startOffset < 0) startOffset = 6; // Dimanche = 0, on veut 6
  
  const paddedDays: (Date | null)[] = Array(startOffset).fill(null);
  return [...paddedDays, ...days];
}

// ============================================================================
// CONSTANTES UTILES
// ============================================================================

export const TODAY = startOfToday();
export const TOMORROW = addDays(TODAY, 1);
export const NEXT_WEEK = addDays(TODAY, 7);
export const NEXT_MONTH = addMonths(TODAY, 1);
export const NEXT_YEAR = addYears(TODAY, 1);

/** Re-export language-aware weekday helpers. Use getWeekdaysShort(lang) / getWeekdaysFull(lang) in components. */
export { getWeekdaysShort, getWeekdaysFull } from "@/lib/i18n/formatLocale";

// ============================================================================
// EXPORTS
// ============================================================================

export {
  addDays,
  subDays,
  addMonths,
  subMonths,
  addYears,
  subYears,
  startOfDay,
  endOfDay,
  startOfToday,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfYear,
  endOfYear,
  eachDayOfInterval,
  differenceInDays,
  format,
  isValid,
} from "date-fns";

